import { FC, useContext } from 'react';

import SSRIcon from '@ingka/ssr-icon';
import calendarIcon from '@ingka/ssr-icon/paths/calendar';
import clickAndCollectIcon from '@ingka/ssr-icon/paths/click-and-collect';
import locationPinIcon from '@ingka/ssr-icon/paths/location-pin';
import { t } from '@onlinepayments/payments-library';

import { ServiceType } from '../../enums/ServiceType';
import { SubOrderType } from '../../enums/SubOrderType';
import DeliveryService from '../../interfaces/DeliveryService';
import { PayorderContext } from '../../PayorderContext';
import { getDeliveryDate } from '../../utils/dateTimeUtils';
import { Address } from '../toolkit';

import OpeningHoursComponent from './OpeningHoursComponent';

interface ICollectProps {
  deliveryService: DeliveryService;
  showOpeningHours: boolean;
}

function getHeader(serviceType: ServiceType, isFullServe: boolean) {
  if (isFullServe) {
    return t('order.collect_full_serve_label');
  }

  switch (serviceType) {
    case ServiceType.ClickCollectStore:
      return t('order.collect_from_store_label');
    case ServiceType.PickupPoint:
    case ServiceType.PickupPointExternal:
      return t('order.collect_from_pick-up_point_label');
    default:
      return t('order.collect_from_label');
  }
}

const CollectComponent: FC<ICollectProps> = ({
  deliveryService: {
    isRangeOfDays,
    deliveryTimeWindows: [timeWindow],
    pickupPoint,
    serviceType,
  },
  showOpeningHours,
}) => {
  const {
    contextState: { orderDetails },
  } = useContext(PayorderContext);

  const isFullServe = orderDetails.response?.subOrderType === SubOrderType.FullServe;

  return (
    <>
      <div className="flex mb-75">
        <SSRIcon className="mr-75" paths={clickAndCollectIcon} />
        <div>{getHeader(serviceType, isFullServe)}</div>
      </div>

      {timeWindow && (
        <div className="flex mb-75">
          <SSRIcon className="mr-75" paths={calendarIcon} />
          <div>
            {t(isFullServe ? 'order.pick_up_date_label' : 'order.available_from_label')}
            <p key={timeWindow.fromLocalDateTime}>{getDeliveryDate(timeWindow, isRangeOfDays)}</p>
          </div>
        </div>
      )}

      {pickupPoint && (
        <>
          <div className="flex mb-75">
            <SSRIcon className="mr-75" paths={locationPinIcon} />
            <Address {...pickupPoint} />
          </div>
          {showOpeningHours && !!pickupPoint.openingHours && (
            <div className="ml-250">
              <OpeningHoursComponent openingHours={pickupPoint.openingHours} />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CollectComponent;
